/**
 * 에이전트 줄 — 창이 AI 패널을 가리고 있을 때도 **그 대화와 계속 이어지게** 한다(2026-10-13, utils/agentBridge).
 *
 *   · 지금 에이전트가 무엇을 하는지(대기 중 / 작업 중)와 마지막 답의 끝부분을 보인다
 *   · 작업 중이면 「중지」 — 패널의 중지와 같은 동작이다(서버 쪽 실행까지 끊긴다)
 *   · 요청 칸 — 같은 대화로 보낸다. 이 창의 지금 내용(`getScreen`)이 그 턴에만 함께 간다
 *
 * 패널이 없으면(닫혀 있거나 AI 제외 빌드) 아무것도 그리지 않는다 — 대화가 없는데 요청 칸만 있으면 보낼 곳이 없다.
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FiSend, FiSquare, FiMessageSquare } from 'react-icons/fi';
import { useAgentBridge, askAgent, stopAgent } from '../../utils/agentBridge';
import './AgentStrip.css';

const TAIL = 280;

function AgentStrip({ getScreen, placeholder }) {
  const { t } = useTranslation();
  const agent = useAgentBridge();
  const [text, setText] = useState('');
  if (!agent.available) return null;

  const send = () => {
    const v = text.trim();
    if (!v || agent.busy) return;
    if (askAgent(v, { screen: getScreen ? getScreen() : '' })) setText('');
    else toast.info(t('assistant:strip.cannotSend'));
  };
  const reply = String(agent.lastReply || '').trim();

  return (
    <div className={`agent-strip${agent.busy ? ' busy' : ''}`} role="region" aria-label={t('assistant:strip.title')}>
      <div className="agent-strip-status">
        <FiMessageSquare size={12} />
        <span className="agent-strip-dot" aria-hidden="true" />
        <strong>{t('assistant:strip.title')}</strong>
        <span className="agent-strip-activity" aria-live="polite">{agent.busy ? (agent.activity || t('assistant:strip.working')) : t('assistant:strip.idle')}</span>
        {agent.busy ? (
          <button type="button" className="app-btn app-btn-danger" onClick={stopAgent}><FiSquare size={11} /> {t('assistant:composer.stop')}</button>
        ) : null}
      </div>
      {reply ? <div className="agent-strip-reply" title={reply.slice(-2000)}>{reply.length > TAIL ? `…${reply.slice(-TAIL)}` : reply}</div> : null}
      <div className="agent-strip-ask">
        <input
          className="agent-strip-input" value={text} maxLength={1000} disabled={agent.busy}
          placeholder={placeholder || t('assistant:strip.placeholder')} aria-label={t('assistant:strip.placeholder')}
          onChange={e => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) send(); }}
        />
        <button type="button" className="app-btn app-btn-primary" disabled={agent.busy || !text.trim()} onClick={send}><FiSend size={11} /> {t('assistant:strip.send')}</button>
      </div>
    </div>
  );
}

AgentStrip.propTypes = {
  /** 이 창의 지금 내용을 글로 — 요청과 함께 그 턴에만 모델에게 간다 */
  getScreen: PropTypes.func,
  placeholder: PropTypes.string,
};

export default AgentStrip;
